// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';
import keyBy from 'lodash/keyBy';

import {SvgIconEl} from 'common/components/svg-icon';
import {TooltipEl} from 'common/components/tooltip';
import {Placement} from 'common/components/tooltip/types';
import {RevealableTextEditEl} from 'common/components/revealable-text-editor';
import {InputSize} from 'common/components/input/types';
import {ButtonEl} from 'common/components/button';
import {Directions} from 'common/components/drop-menu/types';
import {SIZE, TEXT_HIGHLIGHT_THEME, TextHighlightEl} from 'common/components/text-highlight';
import {ButtonTheme} from 'common/components/button/constants';
import {IconSizeType, PATH_SEPARATOR} from 'common/constants';
// eslint-disable-next-line import/no-cycle
import {SelectEl} from 'common/components/select';
// eslint-disable-next-line import/no-cycle
import {SelectTheme} from 'common/components/select/constants';
import {UserIconEl} from 'common/widgets/user-icon';
import {UserListEl} from 'common/widgets/user-list';
import {getBrowseUrl} from 'common/report/util/search';
import {formatDate, parseDate} from 'common/util/date';
import {CONTEXT_PANEL_WIDTH} from 'common/widgets/context-panel/constants';
import {BannerEl} from 'common/components/banner';
import {BannerSize, BannerTheme, BannerType} from 'common/components/banner/types';

import type {ContextPanel} from './index';
import {Detail, DetailType, Metric} from './types';
import {trackDiscoverBackLinkClicked} from './util';

const MAX_DESCRIPTION_DISPLAY_LENGTH = 400;

// 3 icons that show up on context panel for metrics
// this is used as a key
enum DEFAULT_ICON {
  PERSON = `person`,
  FIRE = `fire`,
  PIE = `pie`,
  PULSE = `pulse`,
}

// These are 3 metrics we show in context panel
const defaultMetrics: Record<string, Metric> = {
  person: {
    icon: DEFAULT_ICON.PERSON,
    tooltip: ({t}) => t(`query-builder.context-menu.no-data`),
    value: `-`,
    rank: null,
    rankToolTip: ``,
  },
  fire: {
    icon: DEFAULT_ICON.FIRE,
    tooltip: ({t}) => t(`query-builder.context-menu.no-data`),
    value: `-`,
    rank: null,
    rankToolTip: ``,
  },
  pie: {
    icon: DEFAULT_ICON.PIE,
    tooltip: ({t}) => t(`query-builder.context-menu.no-data`),
    value: `-`,
    rank: null,
    rankToolTip: ``,
  },
  pulse: {
    icon: DEFAULT_ICON.PULSE,
    tooltip: ({t}) => t(`query-builder.context-menu.no-data`),
    value: `-`,
    rank: null,
    rankToolTip: ``,
  },
};

export function template(this: ContextPanel) {
  const {description} = this.state;
  const {
    backgroundColor,
    header,
    subHeader,
    path,
    entityIcon,
    showNewForFreeUsers,
    editItem,
    isUnsaved,
    hasUnsavedChanges,
    isRecentlyUsed,
    definitionDetails,
    definitionRenderer,
    details: rowDetails,
    metrics: measuredMetrics,
    excludedMetricIcons,
    extraInfo,
    hideMetricsSection,
    queriedByUsers,
    noteForFreeUsers,
    rowDetailsReplayPreviewRenderer,
    tags,
    imageInfo,
    verified,
    verifiedAt,
    verifiedBy,
  } = this.params.data;

  const {t, print} = this.getContext(`i18n`);

  const projectIsFreePlan = this.attr(`project-is-free-plan`);

  const HeaderEl = () => {
    return (
      <div sel='.header'>
        {entityIcon ? (
          <div sel='.header-icon'>
            <SvgIconEl attrs={{icon: entityIcon}} />
          </div>
        ) : null}
        <div sel='.header-text'>
          <div sel='.heading' class={{'is-unsaved': isUnsaved}}>
            {path?.length > 0 && (
              <span sel='.path'>{[...path, ``].map((step) => print(step)).join(PATH_SEPARATOR)}</span>
            )}
            <span sel='.leaf'>
              {print(header)}
              {isUnsaved ? `*` : null}
            </span>
          </div>
          {subHeader ? (
            <div sel='.subheader'>
              <div sel='.text'>{print(subHeader)}</div>
            </div>
          ) : null}
        </div>
        {showNewForFreeUsers && projectIsFreePlan && (
          <div sel='.header-show-new-for-free-users'>
            <TextHighlightEl
              attrs={{
                text: t(`query-builder.context-menu.new`),
                theme: TEXT_HIGHLIGHT_THEME.INFORMATION,
              }}
            />
          </div>
        )}
      </div>
    );
  };

  const DefinitionDetailSectionEl = ({details}: {details?: Detail[]}) => {
    if (!details) {
      return null;
    }

    return (
      <div sel='.definition-detail-section'>
        {details.map((detail) => (
          <div sel='.detail'>
            <div sel='.detail-name'>{print(detail.name)}</div>
            <div sel='.detail-value'>{detail.value}</div>
          </div>
        ))}
      </div>
    );
  };

  const RowDetailSectionEl = ({details}: {details?: Detail[]}) => {
    if (!details?.length) {
      return null;
    }

    return (
      <div sel='.row-detail-section'>
        {details.map((detail) =>
          detail.type === DetailType.SessionReplayPreview && rowDetailsReplayPreviewRenderer ? (
            <div sel='.detail-preview'>
              <div sel='.detail-name'>{print(detail.name)}</div>
              <div sel='.detail-value'>{rowDetailsReplayPreviewRenderer(detail.value)}</div>
            </div>
          ) : (
            <div sel='.detail'>
              <div sel='.detail-name'>{print(detail.name)}</div>
              <div sel='.detail-value'>
                {detail.type === DetailType.User ? (
                  <UserIconEl
                    sel='.user-icon'
                    attrs={{
                      size: IconSizeType.XS,
                      'include-label': true,
                      ...detail.value,
                    }}
                  />
                ) : detail.type === DetailType.UserList ? (
                  <UserListEl
                    sel='.user-list'
                    attrs={{
                      size: IconSizeType.XS,
                      'max-icons-displayed': 4,
                      users: detail.value,
                    }}
                  />
                ) : detail.type === DetailType.TextHighlight ? (
                  detail.value.map((item) => (
                    <TextHighlightEl
                      attrs={{
                        theme: TEXT_HIGHLIGHT_THEME.GENERIC,
                        text: item,
                        size: SIZE.SMALL,
                      }}
                    />
                  ))
                ) : detail.type === DetailType.BrowseLink ? (
                  <a
                    sel='.backlink'
                    className={{
                      disabled: detail.value?.count === 0,
                    }}
                    attrs={{
                      href: getBrowseUrl({
                        queryParam: {'event-name-filter': detail.value?.entityNameForBrowse},
                        resetFilters: true,
                      }),
                    }}
                    on={{
                      click: () => {
                        trackDiscoverBackLinkClicked({
                          eventName: header,
                          reportsCount: detail.value?.count,
                        });
                      },
                    }}
                  >
                    {t(`query-builder.context-menu.report-backlinks`, {count: +detail.value?.count || 0})}
                    {` ↗`}
                  </a>
                ) : (
                  detail.value
                )}
              </div>
            </div>
          ),
        )}
      </div>
    );
  };

  const iconsToDisplay = [DEFAULT_ICON.PIE, DEFAULT_ICON.PULSE];

  const metricDataByIcon = keyBy(measuredMetrics || [], `icon`);

  const metrics = iconsToDisplay
    .map((icon) => metricDataByIcon[icon] || defaultMetrics[icon])
    .filter((metric) => !excludedMetricIcons?.includes(metric.icon));

  const FooterEl = () => {
    const verifiedSection =
      verified && verifiedBy ? (
        <div sel='.verify-icon-wrapper'>
          <SvgIconEl sel='.verify-icon' attrs={{icon: `verified-filled`}} />
          <TooltipEl attrs={{placement: Placement.Top}}>
            {t(`query-builder.context-menu.verified-tooltip`, {
              date: formatDate(parseDate(verifiedAt, {iso: true})),
              name: verifiedBy,
            })}
          </TooltipEl>
        </div>
      ) : undefined;

    const metricsSection =
      !hideMetricsSection && metrics?.length ? (
        <div sel='.metrics-section'>
          {metrics.map((metric) => (
            <>
              <div sel='.metric' class={{'empty-metric': metric.value === `-`}}>
                <div sel='.metric-icon' class={{'small-metric-icon': metric.icon === DEFAULT_ICON.PIE}}>
                  <SvgIconEl attrs={{icon: metric.icon}} />
                </div>
                <div sel='.metric-value'>{metric.value || `-`}</div>
                <TooltipEl attrs={{placement: Placement.Top}}>{print(metric.tooltip)}</TooltipEl>
                {metric.rank ? (
                  <div sel='.metric-rank'>
                    <TooltipEl attrs={{placement: Placement.Top}}>{print(metric.rankToolTip)}</TooltipEl>
                    <TextHighlightEl
                      attrs={{
                        theme: TEXT_HIGHLIGHT_THEME.INFORMATION,
                        text: `# ${metric.rank}`,
                        size: SIZE.SMALL,
                      }}
                    />
                  </div>
                ) : null}
              </div>
            </>
          ))}
        </div>
      ) : undefined;

    const queriedBySection = queriedByUsers?.length ? (
      <div sel='.recently-queried-users'>
        <UserListEl
          attrs={{
            size: IconSizeType.Small,
            'max-icons-displayed': 2,
            users: queriedByUsers,
          }}
        />
      </div>
    ) : undefined;

    const entityActionsSection =
      this.helpers.getActionItems().length > 0 ? (
        <div sel='.entity-actions'>
          <SelectEl
            attrs={{
              icon: {leftIcon: `ellipsis`},
              'icon-only': true,
              'menu-width': 200,
              theme: SelectTheme.Tertiary,
              'menu-alignment': Directions.Up,
              elref: `overflowMenu`,
            }}
            class={{'row-actions': true}}
            props={{
              sections: [{items: this.helpers.getActionItems()}],
            }}
            on={{change: (ev: CustomEvent) => this.helpers.handleActionClick(ev.detail.item)}}
          />
        </div>
      ) : undefined;

    if (![verifiedSection, metricsSection, queriedBySection, entityActionsSection].some(Boolean)) {
      return undefined;
    }

    return (
      <div sel='.footer'>
        {verifiedSection}
        {metricsSection}
        <div sel='.footer-spacer' />
        {queriedBySection}
        {entityActionsSection}
      </div>
    );
  };

  const footerText = projectIsFreePlan ? t(noteForFreeUsers) : ``;
  const descriptionAddPlaceholder = `+ ${t(`query-builder.context-menu.description-add-placeholder`)}`;

  return (
    <div
      sel='.root'
      style={{
        backgroundColor,
        '--context-panel-width': `${CONTEXT_PANEL_WIDTH}px`,
      }}
      on={{
        mouseenter: this.helpers.handleMouseEnter,
        mouseleave: this.helpers.handleMouseLeave,
      }}
    >
      <div sel='.body'>
        {hasUnsavedChanges && (
          <BannerEl
            attrs={{
              theme: BannerTheme.Warn,
              type: BannerType.Embedded,
              size: BannerSize.Small,
              multiline: true,
              'no-wrap-content': false,
              'body-text': t(`query-builder.context-menu.unsaved-changes`),
            }}
          />
        )}
        <div sel='.container'>
          {(() => {
            return (
              <>
                <HeaderEl />
                <div sel='.content-body'>
                  {isRecentlyUsed ? (
                    <div sel='.recently-used'>
                      <SvgIconEl attrs={{icon: `time`}} />
                      {t(`query-builder.context-menu.recently-used`)}
                    </div>
                  ) : null}
                  {tags?.length ? (
                    <div sel='.tags'>
                      {tags.map((tag) => (
                        <TextHighlightEl sel='.tag' attrs={{theme: TEXT_HIGHLIGHT_THEME.GENERIC, text: tag}} />
                      ))}
                    </div>
                  ) : null}
                  {description || editItem ? (
                    <div sel='.description'>
                      <RevealableTextEditEl
                        attrs={{
                          text: description,
                          'hide-footer': true,
                          placeholder: t(`query-builder.context-menu.description-placeholder`),
                          'disable-edit': !editItem,
                          size: InputSize.Medium,
                        }}
                        on={{
                          changedText: (ev) => this.helpers.handleTextEditorSave(ev, editItem),
                        }}
                        hook={{
                          insert: this.helpers.editorInserted,
                        }}
                      >
                        {(() => {
                          return (
                            <div sel='.description-content' attrs={{slot: `content`}}>
                              {editItem ? (
                                <div sel='.description-text-wrapper'>
                                  {description
                                    ? this.helpers.formatText(description, {
                                        className: `description-text`,
                                        maxLength: MAX_DESCRIPTION_DISPLAY_LENGTH,
                                      })
                                    : descriptionAddPlaceholder}
                                  <div sel='.editable-icon'>
                                    <ButtonEl
                                      sel='.edit-description-button'
                                      attrs={{
                                        icon: `edit`,
                                        theme: ButtonTheme.Tertiary,
                                      }}
                                    />
                                  </div>
                                </div>
                              ) : (
                                <div sel='.description-text-wrapper'>
                                  {this.helpers.formatText(description, {
                                    className: `description-text`,
                                    maxLength: MAX_DESCRIPTION_DISPLAY_LENGTH,
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </RevealableTextEditEl>
                    </div>
                  ) : null}
                  {imageInfo && imageInfo.length ? (
                    <div sel='.entity-media-section'>
                      {imageInfo.map((info) => (
                        <div sel='.entity-media'>
                          {info.imageUrl ? (
                            <img
                              sel='.entity-image'
                              attrs={{
                                src: info.imageUrl,
                              }}
                              on={{
                                click: () => this.helpers.showImageModalPromise(info.imageUrl),
                              }}
                              style={{
                                cursor: `pointer`,
                              }}
                            />
                          ) : null}
                        </div>
                      ))}
                    </div>
                  ) : null}
                  <DefinitionDetailSectionEl details={definitionDetails} />
                  {definitionRenderer && <div sel='.definition-section'>{definitionRenderer()}</div>}
                  <RowDetailSectionEl details={rowDetails} />
                  {extraInfo && extraInfo.length ? (
                    <div sel='.extra-info'>
                      {extraInfo.map((info) => (
                        <div sel='.info'>
                          <div sel='.icon'>
                            <SvgIconEl attrs={{icon: `info`}} />
                          </div>
                          <div sel='.value'>{print(info)}</div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              </>
            );
          })()}
        </div>
      </div>
      {footerText && <div sel='.footer-text'>{footerText}</div>}
      <FooterEl />
    </div>
  );
}
